/**
 * The dashboard editor's actions, with what they touch passed in, so the page calls them and a test runs them without
 * a page (this repo has no DOM to test in).
 *
 *  - Add widget (#1676): the new widget is selected, so the open config panel edits it and not the widget that was
 *    selected before. Before, only the board changed: the panel stayed bound to the old widget, and the next key picked
 *    in it went into that widget.
 *  - Save (#1675): the editor leaves edit mode only once the server has answered, and says so when it did not.
 *  - Editor review (2026-10-30): a delete can be undone from its toast; Escape closes the settings panel, not while a
 *    field has the keys; a board whose last change has no readable date still draws; and a widget added to a board
 *    whose saved places are odd still goes under the others.
 */

import { format, isValid } from "date-fns";
import { bottomOf } from "./layout-save";
import type { WidgetRegistryEntry } from "./widget-registry";
import type { DashboardConfig, WidgetInstance, WidgetType } from "./types";

/** A new widget of this type, placed below the last one (the grid shoves it around when it collides). */
export function newWidget(
  dashboard: Pick<DashboardConfig, "widgets">,
  type: WidgetType,
  entry: Pick<WidgetRegistryEntry, "name" | "defaultSize">,
  nowMs: number,
): WidgetInstance {
  const maxY = bottomOf(dashboard.widgets);
  return {
    id: `w-${type}-${nowMs}`,
    type,
    title: entry.name,
    layout: { x: 0, y: maxY, w: entry.defaultSize.w, h: entry.defaultSize.h },
  };
}

export interface AddWidgetDeps {
  dashboard: Pick<DashboardConfig, "widgets">;
  type: WidgetType;
  entry: Pick<WidgetRegistryEntry, "name" | "defaultSize">;
  nowMs: number;
  addWidget: (widget: WidgetInstance) => void;
  /** Make this the widget the config panel edits (and the page brings into view). */
  select: (widgetId: string) => void;
  markDirty: () => void;
}

/** Add a widget of this type and select it. Returns the new widget's id. */
export function addWidgetAndSelect(deps: AddWidgetDeps): string {
  const widget = newWidget(deps.dashboard, deps.type, deps.entry, deps.nowMs);
  deps.addWidget(widget);
  deps.select(widget.id);
  deps.markDirty();
  return widget.id;
}

/** Bring a widget into view, by the `data-widget-id` the renderer puts on each. False when it is not on the page. */
export function scrollWidgetIntoView(root: Pick<ParentNode, "querySelectorAll">, widgetId: string): boolean {
  const el = Array.from(root.querySelectorAll("[data-widget-id]")).find(
    (e) => e.getAttribute("data-widget-id") === widgetId,
  );
  if (!el) return false;
  el.scrollIntoView({ block: "nearest" });
  return true;
}

export interface SaveEditDeps {
  /** Writes the board and resolves once the server has it; rejects when it did not. */
  save: () => Promise<void>;
  /** The server has the board: leave edit mode. */
  onSaved: () => void;
  /** The write failed: say so. The editor stays in edit mode with the edit intact. */
  onFailed: (err: unknown) => void;
}

/** Save the edit and wait for the answer. True when the server has it. */
export async function saveBoardEdit(deps: SaveEditDeps): Promise<boolean> {
  try {
    await deps.save();
  } catch (err) {
    deps.onFailed(err);
    return false;
  }
  deps.onSaved();
  return true;
}

export interface DeleteWidgetDeps {
  dashboard: Pick<DashboardConfig, "widgets">;
  widgetId: string;
  removeWidget: (widgetId: string) => void;
  /** Put the widget back as it was: same id, place and settings. */
  addWidget: (widget: WidgetInstance) => void;
  markDirty: () => void;
}

/** Delete a widget. Returns how to undo it, or undefined when the board has no such widget. */
export function deleteWidgetWithUndo(deps: DeleteWidgetDeps): { widget: WidgetInstance; undo: () => void } | undefined {
  const widget = deps.dashboard.widgets.find((w) => w.id === deps.widgetId);
  if (!widget) return undefined;
  deps.removeWidget(widget.id);
  deps.markDirty();
  return {
    widget,
    undo: () => {
      deps.addWidget(widget);
      deps.markDirty();
    },
  };
}

/** Whether Escape closes the settings panel: not when a field, a list or an open menu has the key. */
export function escapeClosesPanel(e: Pick<KeyboardEvent, "key" | "defaultPrevented"> & { target: EventTarget | null }): boolean {
  if (e.key !== "Escape" || e.defaultPrevented) return false;
  const el = e.target as { tagName?: string; isContentEditable?: boolean } | null;
  const tag = el?.tagName?.toLowerCase();
  return !(tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable);
}

/** The board's last change as a date, or a dash when it has none that can be read (date-fns throws on one). */
export function modifiedLabel(updatedAt: string | undefined): string {
  const at = updatedAt ? new Date(updatedAt) : undefined;
  return at && isValid(at) ? format(at, "PP") : "—";
}
